import { Link } from 'react-router-dom';

function renderDiagIcon(id) {
  const props = {
    width: 24,
    height: 24,
    strokeWidth: 2.2,
    stroke: 'var(--teal-primary)',
    fill: 'none',
    style: { display: 'block', flexShrink: 0 }
  };
  switch (id) {
    case 'drop':
      return (
        <svg viewBox="0 0 24 24" {...props}>
          <path d="M12 2L3.5 12.5a8.5 8.5 0 1 0 17 0L12 2z" />
        </svg>
      );
    case 'heart':
      return (
        <svg viewBox="0 0 24 24" {...props}>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'pill':
      return (
        <svg viewBox="0 0 24 24" {...props}>
          <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z" />
          <path d="M12 6v12M6 12h12" />
        </svg>
      );
    case 'flask':
      return (
        <svg viewBox="0 0 24 24" {...props}>
          <path d="M10 2h4M12 2v10M12 12a5 5 0 1 1 0-10M9 12h6" />
        </svg>
      );
    case 'scan':
      return (
        <svg viewBox="0 0 24 24" {...props}>
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case 'list':
      return (
        <svg viewBox="0 0 24 24" {...props} stroke="currentColor">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
      );
    case 'fast':
      return (
        <svg viewBox="0 0 24 24" {...props} stroke="var(--gold-light)" width="32" height="32">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'doctor':
      return (
        <svg viewBox="0 0 24 24" {...props} stroke="var(--gold-light)" width="32" height="32">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case 'report':
      return (
        <svg viewBox="0 0 24 24" {...props} stroke="var(--gold-light)" width="32" height="32">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'walkin':
      return (
        <svg viewBox="0 0 24 24" {...props} stroke="var(--gold-light)" width="32" height="32">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <polyline points="16 11 18 13 22 9" />
        </svg>
      );
    default:
      return null;
  }
}

const bloodTests = [
  { iconId: 'drop', title: 'Complete Blood Count (CBC)', desc: 'Evaluates overall health and detects anemia, infection, inflammation, and leukemia. Measures red cells, white cells, hemoglobin, hematocrit, and platelets.' },
  { iconId: 'pill', title: 'Diabetes Panel', desc: 'Fasting blood sugar, HbA1c, postprandial glucose, and insulin resistance tests to diagnose, monitor, and manage diabetes and pre-diabetic conditions.' },
  { iconId: 'drop', title: 'Liver Function Test (LFT)', desc: 'Assesses liver health by measuring enzymes, proteins, and bilirubin. Detects liver disease, hepatitis, fatty liver, and monitors medication side effects.' },
  { iconId: 'drop', title: 'Kidney Function Test (KFT)', desc: 'Measures creatinine, urea, BUN, and electrolytes to evaluate kidney performance and diagnose chronic kidney disease or urinary tract conditions.' },
  { iconId: 'pill', title: 'Thyroid Profile (T3, T4, TSH)', desc: 'Complete thyroid hormone panel to diagnose hypothyroidism, hyperthyroidism, goiter, and thyroid nodules with high-sensitivity TSH assays.' },
  { iconId: 'heart', title: 'Cardiac Risk Profile', desc: 'Cholesterol, triglycerides, LDL, HDL, VLDL, CRP, and troponin to assess cardiovascular risk and monitor heart health after cardiac events.' },
  { iconId: 'pill', title: 'Hormonal Assays', desc: 'Reproductive hormones including FSH, LH, prolactin, estradiol, testosterone, progesterone — for fertility evaluation, PCOS, and hormonal disorders.' },
  { iconId: 'shield', title: 'Hepatitis Panel', desc: 'Hepatitis A, B, and C antibody and antigen tests to screen for viral hepatitis infections, assess immunity, and monitor treatment response.' },
  { iconId: 'shield', title: 'Infection & Inflammation', desc: 'CRP, ESR, blood cultures, typhoid (Widal), dengue NS1 antigen, malaria antigen, and complete urine examination for infection workup.' },
  { iconId: 'pill', title: 'Vitamin & Mineral Panels', desc: 'Vitamin D, Vitamin B12, Iron studies (serum iron, TIBC, ferritin), calcium, magnesium, and zinc levels to detect deficiencies.' },
  { iconId: 'drop', title: 'Pregnancy Tests', desc: 'Quantitative beta-hCG, antenatal screening (TORCH), blood group and Rh typing, indirect Coombs test, and triple/quadruple markers.' },
  { iconId: 'flask', title: 'Urine & Stool Analysis', desc: 'Complete urine examination (CUE), urine culture & sensitivity, stool routine examination, occult blood, and stool culture.' }
];

const imagingServices = [
  { iconId: 'scan', title: 'Abdominal Ultrasound', desc: 'Detailed imaging of the liver, gallbladder, spleen, pancreas, kidneys, and bladder. Used to detect gallstones, fatty liver, renal cysts, and abdominal masses.' },
  { iconId: 'scan', title: 'Obstetric Ultrasound', desc: 'Pregnancy dating, fetal anatomy survey, NT scan, anomaly scan (TIFFA), fetal growth monitoring, and placenta assessment. 2D and 4D imaging available.' },
  { iconId: 'scan', title: 'Pelvic Ultrasound', desc: 'Transvaginal and transabdominal pelvic ultrasound for ovarian cysts, PCOS, uterine fibroids, endometriosis, and follicular monitoring during fertility treatment.' },
  { iconId: 'scan', title: 'Colour Doppler Scans', desc: 'Assesses blood flow in arteries and veins. Used for deep vein thrombosis (DVT), carotid artery stenosis, renal artery Doppler, and fetal well-being assessment.' },
  { iconId: 'scan', title: 'Neonatal & Pediatric', desc: 'Cranial ultrasound for newborns, hip ultrasound for developmental dysplasia, abdominal ultrasound for pyloric stenosis, and renal tract in children.' },
  { iconId: 'scan', title: 'Small Parts & MSK', desc: 'High-frequency ultrasound of the thyroid, breast, lymph nodes, testes, soft tissue masses, joints, and tendons with guided aspiration or biopsy.' }
];

function BloodTest() {
  return (
    <>
      {/* Page Hero */}
      <section className="page-hero" style={{ backgroundImage: 'linear-gradient(rgba(8, 50, 62, 0.85), rgba(8, 50, 62, 0.85)), url(/images/hero_blood_test.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container">
          <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Pathology & Imaging</div>
          <h1>Blood Test & Ultrasound Imaging</h1>
          <p>Under the trade name Mega Lab & Digno Research Center</p>
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="sep">›</span>
            <span className="current">Pathology & Imaging</span>
          </div>
        </div>
      </section>

      {/* Diagnostic Center Intro with Image */}
      <section className="section-pad" style={{ background: 'var(--white)', paddingBottom: '20px' }}>
        <div className="container">
          <div className="grid-2" style={{ alignItems: 'center', gap: '48px' }}>
            <div>
              <div className="eyebrow">World-Class Facilities</div>
              <h2 style={{ color: 'var(--teal-dark)' }}>Pathology & Ultrasound Imaging Services</h2>
              <p style={{ marginTop: '16px', lineHeight: '1.7', color: 'var(--muted)' }}>
                We provide high-quality Pathology and Ultrasound Imaging Services across numerous cities, towns, and villages throughout India. Our mission is to make reliable and affordable diagnostic healthcare accessible to every community, regardless of location.
              </p>
              <p style={{ lineHeight: '1.7', color: 'var(--muted)' }}>
                Our Radiology Department is equipped with advanced ultrasound imaging technology and is staffed by experienced, certified sonologists and radiologists, ensuring accurate and timely diagnostic results.
              </p>
              <p style={{ lineHeight: '1.7', color: 'var(--muted)' }}>
                Our Pathology Department features modern laboratory equipment and a team of highly qualified professionals capable of performing a comprehensive range of blood tests and diagnostic investigations in-house. We are committed to delivering precise, reliable, and timely reports while maintaining the highest standards of quality.
              </p>
              <p style={{ lineHeight: '1.7', color: 'var(--muted)' }}>
                At Mega Health Group(Mega Hospital Foundation), our primary goal is to provide affordable healthcare services to all. We also offer subsidized and charitable medical services to patients facing financial hardship, ensuring that quality healthcare remains accessible to everyone.
              </p>
              <p style={{ lineHeight: '1.7', color: 'var(--muted)' }}>
                Community service is at the heart of our mission. We regularly organize free health camps, medical awareness programs, educational workshops, blood donation drives, and public health campaigns to promote preventive healthcare and healthier communities. Through these initiatives, we strive to improve public health awareness and ensure that no one is denied essential medical care because of financial limitations.
              </p>
            </div>
            <div>
              <img src="/images/lab_diagnostics.png" alt="Advanced Diagnostic Laboratory" style={{ width: '100%', aspectRatio: '16/10', objectFit: 'cover', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <div style={{ background: 'var(--gold-primary)', padding: '16px 0', boxShadow: 'var(--shadow-sm)' }}>
        <div className="container" style={{ display: 'flex', gap: '24px 32px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.88rem', color: 'var(--teal-dark)' }}>✦ Results in 4–6 Hours</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.88rem', color: 'var(--teal-dark)' }}>✦ ISO-Certified Laboratory</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.88rem', color: 'var(--teal-dark)' }}>✦ Online Result Access</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.88rem', color: 'var(--teal-dark)' }}>✦ Home Sample Collection Available</div>
        </div>
      </div>

      {/* Blood Tests Section */}
      <section className="section-pad" id="blood-tests" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div className="section-header center">
            <div className="eyebrow">Lab Services</div>
            <h2>Blood Test Services</h2>
            <p>Our state-of-the-art laboratory offers a comprehensive panel of diagnostic blood tests with quick turnaround and precise results.</p>
          </div>
          
          <div className="blood-tests-grid">
            {bloodTests.map(test => (
              <div key={test.title} className="test-card">
                <div className="icon" style={{ height: 32, display: 'flex', alignItems: 'center', marginBottom: 16 }}>{renderDiagIcon(test.iconId)}</div>
                <h4 style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{test.title}</h4>
                <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginBottom: 0, marginTop: 8, lineHeight: 1.6 }}>{test.desc}</p>
              </div>
            ))}
          </div>

          {/* Patient Guidance Panel */}
          <div style={{ background: 'var(--teal-soft)', borderRadius: 'var(--radius-lg)', padding: '36px', marginTop: '48px', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--teal-dark)', fontSize: '1.45rem', display: 'flex', alignItems: 'center', gap: 10 }}>
              {renderDiagIcon('list')} Before Your Blood Test — Patient Guidance
            </h3>
            <div className="grid-3" style={{ gap: 24 }}>
              <div>
                <h4 style={{ fontSize: '1.05rem', marginBottom: '8px', color: 'var(--teal-dark)' }}>Fasting Tests</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0 }}>
                  For blood glucose, lipid panel, liver function, and iron tests — fast for 8–12 hours. Normal drinking water is allowed. Take regular medications unless advised otherwise by your physician.
                </p>
              </div>
              <div>
                <h4 style={{ fontSize: '1.05rem', marginBottom: '8px', color: 'var(--teal-dark)' }}>What to Bring</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0 }}>
                  Please bring your doctor's test request slip, any previous reports for comparison, and your CNIC or patient ID card. Arrive 10 minutes early to complete registration.
                </p>
              </div>
              <div>
                <h4 style={{ fontSize: '1.05rem', marginBottom: '8px', color: 'var(--teal-dark)' }}>Receiving Results</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0 }}>
                  Most routine test results are ready in 4–6 hours. Urgent tests are processed within 2 hours. Results can be collected in person, sent via WhatsApp, or accessed on our online portal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div style={{ height: '4px', background: 'linear-gradient(90deg, var(--teal-primary), var(--gold-primary), var(--teal-primary))' }}></div>

      {/* Ultrasound Imaging Section */}
      <section className="section-pad" id="imaging" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="section-header center">
            <div className="eyebrow">Radiology</div>
            <h2>Ultrasound & Imaging Services</h2>
            <p>Our radiology department is equipped with advanced imaging technology operated by certified sonologists and radiologists for precise diagnostic results.</p>
          </div>

          <div className="imaging-grid">
            {imagingServices.map(service => (
              <div key={service.title} className="imaging-card">
                <div className="imaging-icon" style={{ height: 32, display: 'flex', alignItems: 'center', marginBottom: 12 }}>{renderDiagIcon(service.iconId)}</div>
                <div>
                  <h4 style={{ color: 'var(--teal-dark)', fontWeight: 700 }}>{service.title}</h4>
                  <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginBottom: 0, marginTop: 6, lineHeight: 1.6 }}>{service.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Equipment Info */}
          <div style={{ background: 'var(--teal-dark)', borderRadius: 'var(--radius-lg)', padding: '40px', marginTop: '48px', color: 'white', boxShadow: 'var(--shadow-lg)' }}>
            <div className="grid-2" style={{ gap: 40, alignItems: 'center' }}>
              <div>
                <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Our Technology</div>
                <h3 style={{ color: 'white', marginBottom: '16px', fontSize: '1.65rem' }}>Advanced Imaging Systems</h3>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.92rem', lineHeight: 1.7 }}>
                  We utilize high-resolution medical ultrasound machines with Color and Power Doppler modules, real-time 3D/4D fetal imaging, and automated clinical measurement tools. This guarantees highly accurate, detailed diagnostic reports.
                </p>
                <ul style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px', listStyle: 'none' }}>
                  <li style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}>✦ Broad-band high-frequency multi-frequency probes</li>
                  <li style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}>✦ Real-time 2D, 3D & 4D gynecological scanning</li>
                  <li style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}>✦ High-sensitivity blood flow mapping via Color Doppler</li>
                  <li style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}>✦ Secured digital report database for records tracking</li>
                </ul>
              </div>
              <div className="grid-2" style={{ gap: 16 }}>
                <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ marginBottom: '12px' }}>{renderDiagIcon('fast')}</div>
                  <div style={{ fontWeight: 700, color: 'var(--gold-light)', fontSize: '1.05rem' }}>Same Day</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>Written Reports</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ marginBottom: '12px' }}>{renderDiagIcon('doctor')}</div>
                  <div style={{ fontWeight: 700, color: 'var(--gold-light)', fontSize: '1.05rem' }}>Female Doctor</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>Available on Request</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ marginBottom: '12px' }}>{renderDiagIcon('report')}</div>
                  <div style={{ fontWeight: 700, color: 'var(--gold-light)', fontSize: '1.05rem' }}>Full Scans</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>Printed Images Included</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ marginBottom: '12px' }}>{renderDiagIcon('walkin')}</div>
                  <div style={{ fontWeight: 700, color: 'var(--gold-light)', fontSize: '1.05rem' }}>Walk-ins</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>Referrals Welcome</div>
                </div>
              </div>
            </div>
          </div>

          {/* Ultrasound Patient Prep */}
          <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '36px', marginTop: '32px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--teal-dark)', fontSize: '1.45rem', display: 'flex', alignItems: 'center', gap: 10 }}>
              {renderDiagIcon('list')} Preparing for Your Ultrasound Scan
            </h3>
            <div className="grid-3" style={{ gap: 24 }}>
              <div style={{ padding: '20px', background: 'var(--bg-light)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ fontSize: '1rem', color: 'var(--teal-dark)', marginBottom: '8px' }}>Abdominal Scan Prep</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0, lineHeight: 1.65 }}>
                  Do not eat any food for 6 hours prior to your scan. Drink 3–4 glasses of water 1 hour before your appointment and do not empty your bladder, as a full bladder is necessary to visualize organs clearly.
                </p>
              </div>
              <div style={{ padding: '20px', background: 'var(--bg-light)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ fontSize: '1rem', color: 'var(--teal-dark)', marginBottom: '8px' }}>Pelvic & Pregnancy Prep</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0, lineHeight: 1.65 }}>
                  Drink 3–4 glasses of water 45 minutes before the scan and hold your urine. For first-trimester scans, a full bladder is mandatory. Late-pregnancy scans do not require special preparation.
                </p>
              </div>
              <div style={{ padding: '20px', background: 'var(--bg-light)', borderRadius: 'var(--radius)' }}>
                <h4 style={{ fontSize: '1rem', color: 'var(--teal-dark)', marginBottom: '8px' }}>Thyroid, Breast, & Joints</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0, lineHeight: 1.65 }}>
                  No dietary restrictions or bladder preparation required. We advise wearing comfortable, loose-fitting two-piece clothing, and removing any necklaces, chains, or jewelry from the scanning area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Book Test CTA */}
      <section className="section-pad" style={{ background: 'linear-gradient(135deg, var(--teal-dark) 0%, var(--teal-primary) 100%)', color: 'white', textAlign: 'center' }}>
        <div className="container">
          <div className="eyebrow" style={{ color: 'var(--gold-light)' }}>Get Tested Today</div>
          <h2 style={{ color: 'white', marginBottom: '16px' }}>Book a Diagnostic Test or Scan</h2>
          <p style={{ color: 'rgba(255,255,255,0.72)', maxWidth: '520px', margin: '0 auto 28px' }}>
            Walk in or call ahead to schedule your lab test or ultrasound session. Home sample collection is available for blood tests.
          </p>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/contact" className="btn btn-gold">Schedule Scan / Draw</Link>
            <a href="tel:+921234567890" className="btn btn-secondary" style={{ color: 'white', borderColor: 'white', background: 'transparent' }}>Call Laboratory</a>
          </div>
        </div>
      </section>
    </>
  );
}

export default BloodTest;
